import React, { useState } from "react";
import LocationForm from "./LocationForm";
import { useGetLocationsQuery } from "../../redux/services/locationsApi";
import TopScrollTable from "../../components/TopScrollTable";
import {
  Typography,
  Tag,
  Space,
  Button,
  Row,
  Col,
  notification,
  Tabs,
  Checkbox
} from "antd";
const { Text } = Typography;

import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import LocationFilters from "./LocationFilters";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const Location = () => {
  const [api, contextHolder] = notification.useNotification();

  const [isOpen, setOpen] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<any>({});

  const [queryParams, setQueryParams] = useState<any>({ include_all: true, page: 1, page_size: 20 });

  const { data, isFetching, isLoading, refetch } = useGetLocationsQuery({
    ...queryParams
  });

  const locations = data?.locations || [];

  const onUpdate = (location: any) => {
    setSelectedLocationData(location);
    setOpen(!isOpen);
  };

  const onShowCreateForm = () => {
    setSelectedLocationData(null);
    setOpen(true);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code"
    },
    {
      title: "Parent",
      dataIndex: "parent",
      key: "parent",
      render: (parent) => <Text>{parent ? parent?.name : "-"}</Text>
    },
    {
      title: "Location Type",
      dataIndex: "location_type",
      key: "location_type",
      render: (location_type) => <Text>{location_type ? location_type?.toUpperCase() : "-"}</Text>
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, location: any) => (
        <Space size="middle">
          <Link to={`${location?.id}`}>
            <Button icon={<EyeOutlined />} title="View"></Button>
          </Link>
          <Button
            icon={<EditOutlined />}
            onClick={() => onUpdate(location)}
            title="Update"
          ></Button>
        </Space>
      )
    }
  ];

  const onTabChange = (key: string) => {
    const values: any = { location_type: key, page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  const pagination = {
    pageSize: queryParams?.page_size,
    current: queryParams?.page,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onShowSizeChange: (current: any, page_size: number) => {
      const values: any = { page_size: page_size };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    },
    onChange: (page: number) => {
      const values: any = { page: page };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const refetchWithReset = () => {
    const values: any = { page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    refetch();
  };

  const onChangeActiveOnly = (e: CheckboxChangeEvent) => {
    const include_all: boolean = e.target.checked;
    const values: any = { include_all: !include_all };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  return (
    <>
      {contextHolder}

      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Locations</h2>
            <Text>Total locations: {data?.meta?.total_count || 0}</Text>
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            <Button onClick={onShowCreateForm} size="large" type="primary">
              Create Location
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "",
                label: "All"
              },
              {
                key: "city",
                label: "City"
              },
              {
                key: "state",
                label: "State"
              },
              {
                key: "zone",
                label: "Zone"
              }
            ]}
            onChange={onTabChange}
          />
        </Col>
      </Row>

      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Checkbox onChange={onChangeActiveOnly}>Show Active Only</Checkbox>
        </Col>
      </Row>

      <LocationFilters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
      />

      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={locations}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />

      {isOpen && (
        <LocationForm
          setOpen={setOpen}
          locationId={selectedLocationData?.id}
          isOpen={isOpen}
          refetch={refetchWithReset}
        />
      )}
    </>
  );
};

export default Location;
